var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { useAppSelector } from 'hooks/reduxHooks';
import { getCurrentSenior, getQueryParamTimezone, } from 'globals/global.functions';
import { AssessmentStatuses, EventViewState, Roles } from 'globals/enums';
import RoleBaseRender from 'common/RoleBaseRender';
import { subHeaderStyle } from './SubHeader.style';
import ErrorIcon from 'assets/icons/ErrorIcon.svg';
import { usePauseStatus } from 'pages/WCPages/SeniorDashboard/PauseContext';
var SubHeader = React.memo(function (props) {
    var navigate = useNavigate();
    var location = useLocation();
    var classes = subHeaderStyle().classes;
    var _a = useState('dashboard'), currentRoute = _a[0], setCurrentRoute = _a[1];
    var _b = usePauseStatus(), isProfilePaused = _b.isProfilePaused, isDevicePaused = _b.isDevicePaused, isNotificationPaused = _b.isNotificationPaused;
    var pauseBadgeLabel = isProfilePaused
        ? 'Member Paused - View Only'
        : isDevicePaused
            ? 'Device Paused'
            : isNotificationPaused
                ? 'Notification Paused'
                : null;
    var alert = useAppSelector(function (state) { return state.events.alert; });
    var summary = useAppSelector(function (state) { return state.events.summary; });
    var userRole = useAppSelector(function (state) { return state.auth.userRole; });
    var roleConfig = useAppSelector(function (state) { return state.auth.roleConfig; });
    var careInsight = __assign(__assign({}, alert), summary);
    var seniorId = useAppSelector(function (state) { return state.common.seniorDetail.minimalInfo.user_id; });
    var assessmentStatuses = useAppSelector(function (state) { return state.assessments.assessmentStatuses; });
    var isAssessmentIncomplete = React.useMemo(function () {
        var res = Object.keys(assessmentStatuses).some(function (assessmentKey) {
            var _a;
            var status = (_a = assessmentStatuses[assessmentKey]) === null || _a === void 0 ? void 0 : _a.status;
            // for wellness-survey only consider Incomplete Status
            if (assessmentKey === 'wellness-survey') {
                return status === AssessmentStatuses.INCOMPLETE;
            }
            // for holistic and care give strain assessments only consider complete status
            var disabledAssessments = [
                'holistic-assessment',
                'caregiver-strain-assessment',
                'ucla_loneliness_assessment',
                'member_questionnaire_1',
                'member_questionnaire_2',
                'member_questionnaire_3',
            ];
            if (disabledAssessments.includes(assessmentKey))
                return false;
            // for all others consider Incomplete Status & Due Status
            return (status === AssessmentStatuses.DUE ||
                status === AssessmentStatuses.INCOMPLETE);
        });
        return res;
    }, [assessmentStatuses]);
    var isActionNotification = Object.values(careInsight).some(function (data) {
        return data.viewState === EventViewState.ActionNotification &&
            data.seniorId === seniorId;
    });
    var lists = [
        {
            name: 'dashboard',
            label: 'Dashboard',
            roles: [
                Roles.Admin,
                Roles.CareAgent,
                Roles.Admin_WellnessServices,
                Roles.FACILITY_AGENT,
                Roles.BPO_CareUser
            ],
        },
        {
            name: 'onboarding-info',
            label: 'Onboarding Info',
            roles: [
                Roles.Admin,
                Roles.CareAgent,
                Roles.WarehouseEmployee,
                Roles.BDM,
                Roles.Admin_WellnessServices,
                Roles.BPO_CareUser
            ],
        },
        {
            name: 'wellness-plan',
            label: 'Wellness Plan',
            roles: [
                Roles.Admin,
                Roles.CareAgent,
                Roles.Admin_WellnessServices,
                Roles.FACILITY_AGENT,
            ],
        },
        {
            name: 'assessments',
            defaultPath: '/wellness-survey',
            label: 'Assessments',
            roles: [Roles.Admin, Roles.CareAgent, Roles.Admin_WellnessServices],
            showIncomplete: isAssessmentIncomplete,
        },
        {
            name: 'scheduler',
            label: 'Scheduler',
            roles: [Roles.Admin, Roles.CareAgent, Roles.Admin_WellnessServices],
        },
        {
            name: 'call-schedule',
            label: 'Call Schedule',
            roles: [Roles.Admin, Roles.CareAgent, Roles.Admin_WellnessServices],
        },
        {
            name: 'call-logs',
            label: 'Notes log',
            roles: [Roles.Admin, Roles.CareAgent, Roles.Admin_WellnessServices, Roles.BPO_CareUser, Roles.FACILITY_AGENT],
        },
        {
            name: 'care-insights',
            label: 'Care Insights',
            defaultPath: '/message-manager',
            showNotification: isActionNotification,
            roles: [Roles.Admin, Roles.CareAgent, Roles.Admin_WellnessServices],
        },
        {
            name: 'senior-location',
            label: 'Location',
            roles: [Roles.Admin, Roles.CareAgent, Roles.Admin_WellnessServices, Roles.BPO_CareUser],
        },
        // {
        //   name: 'case-management',
        //   label: 'Case Management',
        //   roles: [Roles.CareAgent, Roles.Admin_WellnessServices],
        // },
    ];
    /**
     * @function changeRoute
     * @description method to change the route
     * @param {*} path
     * @param {*} defaultPath
     * @returns void
     */
    var changeRoute = function (path, defaultPath) {
        if (defaultPath === void 0) { defaultPath = ''; }
        // Facility resident pages use a different URL hierarchy than care-agent
        // senior pages. Keep navigation inside that hierarchy when the subheader
        // is rendered from a facility resident dashboard.
        var pathSegments = location.pathname.split('/').filter(Boolean);
        if (pathSegments[0] === 'facility-management' && pathSegments.length >= 6) {
            var facilityResidentBasePath = "/".concat(pathSegments.slice(0, 6).join('/'));
            var destination = path === 'dashboard'
                ? facilityResidentBasePath
                : "".concat(facilityResidentBasePath, "/").concat(path).concat(defaultPath);
            navigate(destination, { replace: true });
            return;
        }
        var seniorInfo = __assign({}, getCurrentSenior());
        navigate("/senior/".concat(seniorInfo.seniorID, "/").concat(seniorInfo.accountID, "/").concat(getQueryParamTimezone(seniorInfo.timezone), "/").concat(path).concat(defaultPath), { replace: true });
    };
    useEffect(function () {
        lists.every(function (_a) {
            var name = _a.name;
            if (location.pathname.includes(name)) {
                setCurrentRoute(name);
                return false;
            }
            else {
                return true;
            }
        });
        if (userRole.includes(Roles.WarehouseEmployee)
        //||userRole.includes(Roles.BDM)
        ) {
            changeRoute(roleConfig.defaultHomeRoute);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (Array.isArray(userRole)) {
        if (userRole.includes(Roles.BDM)) {
            return null;
        }
    }
    if (props.noHeader) {
        return null;
    }
    return (_jsxs(Box, { mb: '1em', className: classes.container, position: "relative", children: [lists.map(function (listData, index) {
                var isCaseManagement = listData.name === 'case-management';
                var isAllowed = listData.roles.some(function (r) { return userRole.includes(r); }) &&
                    !(isCaseManagement && userRole.includes(Roles.Admin));
                if (!isAllowed)
                    return null;
                return (_jsx(RoleBaseRender, { forRole: listData.roles, children: _jsx(Lists, { name: listData.name, defaultPath: listData.defaultPath, label: listData.label, listItem: lists.length, currentRoute: currentRoute, changeRoute: changeRoute, disabled: listData.disabled ? listData.disabled : false, marginRight: index !== lists.length - 1, showNotification: listData.showNotification, showIncomplete: listData.showIncomplete }, listData.name) }, listData.name));
            }), pauseBadgeLabel && currentRoute === 'dashboard' && (
            // Adding component="span" stops it from being targeted by the container's div rule
            _jsx(Box, { component: "span", className: classes.pauseBadge, children: pauseBadgeLabel }))] }));
});
var Lists = function (_a) {
    var _b;
    var name = _a.name, defaultPath = _a.defaultPath, label = _a.label, listItem = _a.listItem, currentRoute = _a.currentRoute, changeRoute = _a.changeRoute, disabled = _a.disabled, marginRight = _a.marginRight, showNotification = _a.showNotification, showIncomplete = _a.showIncomplete;
    var classes = subHeaderStyle().classes;
    return (_jsxs(Box, { mr: marginRight ? 1 : 0, style: { position: 'relative' }, children: [_jsxs(Button, { className: clsx([classes.button], (_b = {},
                    _b[classes.filled] = currentRoute == name,
                    _b)), disabled: disabled, onClick: function () { return changeRoute(name, defaultPath); }, children: [label, showIncomplete && (_jsx(Box, { className: classes.incomplete, children: _jsx("img", { src: ErrorIcon, width: '21', height: '21' }) }))] }), showNotification && _jsx(Box, { className: classes.notification })] }));
};
export { SubHeader };
